import { Distance } from '@/types/Distance';
import { SearchSortAndDirection } from '@/types/search/enums/sortTypes';

export const SEARCH_QUERY_MAX_LENGTH = 100;

export enum SearchQueryStatus {
    Done = 'done',
    Live = 'live',
    Online = 'online',
    Passed = 'passed',
    Sold = 'sold',
    Upcoming = 'upcoming',
}

export type SearchQueryOptions = {
    auctionHouse?: string[];
    shipping?: [string];
    status: SearchQueryStatus[];
    topRated?: boolean;
};

/**
 * Per-request overrides for a single facet. Lets a caller ask the server
 * for a larger bucket count (View More) or trigger AuctionHouse
 * search-as-you-type. search-party silently clamps `size` to the per-facet
 * cap and ignores `searchString` for any facet other than AuctionHouse.
 */
export type FacetOverride = {
    size?: number;
    searchString?: string;
};

export type FacetOverrides = {
    [facetId: string]: FacetOverride;
};

export type SearchQueryDateRange = {
    max?: number;
    maxSet?: boolean;
    min?: number;
    minSet?: boolean;
};

export type SearchQuery = {
    analyticsTags: string[];
    buyNow: boolean | undefined;
    buyNowDate: number | undefined;
    campaignId: string | undefined;
    /**
     * Filters on House.Retail. true keeps retail houses, false keeps non-retail houses, omitted
     * applies no retail filter at all. Added to v4 lot search in search-party#483.
     */
    retail?: boolean;
    catalogId?: number;
    categories?: string[];
    citySlug?: string;
    distance: Distance;
    domesticFlatShipping: boolean | undefined;
    /**
     * Auction house ids this page must never show, independent of the viewer. Unioned with the
     * signed-in user's own hidden houses by `getAugmentedSearchQuery` into
     * `options.auctionHouse[0].exclude`, and stripped before the request is sent.
     */
    excludedAuctionHouseIds?: string[];
    facetOverrides?: FacetOverrides;
    filterTerm?: string;
    fullSearch?: boolean;
    lat?: string;
    long?: string;
    options: SearchQueryOptions;
    page: number;
    pageSize: number;
    promoted?: boolean;
    publishDate: SearchQueryDateRange;
    ranges?: { [key: string]: SearchQueryDateRange };
    region?: string;
    saleDate: SearchQueryDateRange;
    searchTerm: string;
    seoSearch?: boolean;
    similarItems?: boolean;
    sort: SearchSortAndDirection;
    topRated?: boolean;
    useGeo?: boolean;
};

export type AugmentedSearchQueryOption = {
    exclude: string[];
    include: string[];
};

export type AugmentedSearchQueryOptions = {
    auctionHouse?: [AugmentedSearchQueryOption];
    shipping?: [string];
    status: SearchQueryStatus[];
    topRated?: boolean;
};

export type AugmentedSearchQuery = {
    analyticsTags: string[];
    buyNow: boolean;
    buyNowDate: number;
    campaignId: string;
    catalogId?: number;
    categories?: string[];
    distance: Distance;
    domesticFlatShipping: boolean;
    facetOverrides?: FacetOverrides;
    filterTerm?: string;
    fullResults?: boolean;
    lat?: string;
    long?: string;
    options: AugmentedSearchQueryOptions;
    page: number;
    pageSize: number;
    promoted?: boolean;
    publishDate: SearchQueryDateRange;
    ranges?: { [key: string]: SearchQueryDateRange };
    saleDate: SearchQueryDateRange;
    searchTerm: string;
    // SEO search is a flag to indicate that the search is a SEO search page and will return breadcrumbs
    seoSearch?: boolean;
    sort: string;
    topRated?: boolean;
    useGeo?: boolean;
};

export enum SearchPageName {
    CATALOG = 'catalog',
    COLLECTIONS = 'collections',
    PRICE_GUIDE = 'price-guide',
    SEARCH = 'search',
    SEO_SEARCH = 'seo-search',
    SOLD_SEARCH = 'sold-search',
}
